import { useTranslation } from 'react-i18next';

import type { IconName } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import { useAppState } from '../../../AppStateProvider';
import { Switch } from '../../../components/Switch';
import type { SettingsKey, SettingsAppStorage } from '../../../domain/settings';
import {
  AUTO_OPEN_LINKEDIN_KEY,
  ENABLE_EVERYWHERE_KEY,
  ENABLE_TOOLS_KEY,
  ENABLE_CRM_KEY,
  ENABLE_GCALENDAR_KEY,
  ENABLE_GMAIL_KEY,
} from '../../../domain/settings';
import { appStore } from '../../../services/appStore';
import UrlService from '../../../services/UrlService';
import { setSettingsItems } from '../../../services/windowMessaging';
import { useSettingsTracking } from '../useSettingsTracking';

import {
  StyledSettingsBlock,
  StyledSettingsItem,
  StyledSettingsItemTitleWrapper,
  StyledSettingsItemTitle,
  StyledSettingsItemText,
  StyledSettingsItemIcon,
  StyledSettingsItemTextDescription,
  StyledNewTag,
} from './styles';

const settings: {
  key: keyof SettingsAppStorage;
  icon: IconName;
  text: string;
  description?: string;
  isNew?: boolean;
}[] = [
  {
    key: AUTO_OPEN_LINKEDIN_KEY,
    icon: 'arrows-vector',
    text: 'settings.auto_open_linkedin',
  },
  {
    key: ENABLE_TOOLS_KEY,
    icon: 'serp_product_grid',
    text: 'settings.enable_calendar',
    description: 'settings.enable_calendar.description',
  },
  {
    key: ENABLE_GCALENDAR_KEY,
    icon: 'daily-ranking',
    text: 'settings.enable_google_calendar',
    description: 'settings.enable_google_calendar.description',
    isNew: true,
  },
  {
    key: ENABLE_GMAIL_KEY,
    icon: 'email',
    text: 'settings.enable_gmail',
    description: 'settings.enable_gmail.description',
    isNew: true,
  },
  {
    key: ENABLE_CRM_KEY,
    icon: 'crm',
    text: 'settings.enable_crm',
    description: 'settings.enable_crm.description',
  },
  {
    key: ENABLE_EVERYWHERE_KEY,
    icon: 'global-rank',
    text: 'settings.enable_everywhere',
    description: 'settings.enable_everywhere_description',
  },
];

export const SettingsBlock = () => {
  const { t } = useTranslation();
  const { url } = useAppState();
  const { handleTrackSettingsChange } = useSettingsTracking();

  const handleSettingChange = (key: SettingsKey) => {
    const currentValue = !!appStore.getSettingsStorageValue(key);
    const newValue = !currentValue;

    handleTrackSettingsChange(key, newValue);

    setSettingsItems({ [key]: newValue });
    appStore.setSettingsStorageValue({ [key]: newValue });

    if (key === ENABLE_TOOLS_KEY && !newValue && url && UrlService.isOutlookCalendarUrl(new URL(url))) {
      window.parent.postMessage({ message: 'hide icon' }, '*');
    }
    if (key === ENABLE_GCALENDAR_KEY && !newValue && url && UrlService.isGoogleCalendarUrl(new URL(url))) {
      window.parent.postMessage({ message: 'hide icon' }, '*');
    }
  };

  return (
    <StyledSettingsBlock data-automation="SettingsBlock">
      {settings.map(({ key, icon, text, description, isNew }) => (
        <div key={key}>
          <StyledSettingsItem>
            <StyledSettingsItemTitleWrapper>
              <StyledSettingsItemTitle data-automation="SettingsItemTitle">
                <StyledSettingsItemIcon iconName={icon} size="xs" iconColor={colorsPalettes.carbon[500]} />
                <StyledSettingsItemText>{t(text)}</StyledSettingsItemText>
              </StyledSettingsItemTitle>
            </StyledSettingsItemTitleWrapper>
            {isNew ? <StyledNewTag /> : null}
            <Switch
              isSelected={!!appStore.getSettingsStorageValue(key)}
              onClick={() => handleSettingChange(key as SettingsKey)}
            />
          </StyledSettingsItem>
          {description ? <StyledSettingsItemTextDescription>{t(description)}</StyledSettingsItemTextDescription> : null}
        </div>
      ))}
    </StyledSettingsBlock>
  );
};
